import { BREAKPOINTS, type Breakpoint, type TileItem, type TileLayout } from "@csbi/contracts"

export const COLS: Record<Breakpoint, number> = { lg: 12, md: 8, sm: 4 }

export const BREAKPOINT_PX: Record<Breakpoint, number> = { lg: 1024, md: 640, sm: 0 }

export const ROW_HEIGHT = 40
export const MARGIN: [number, number] = [12, 12]

export type GridItem = {
  i: string
  x: number
  y: number
  w: number
  h: number
  static: boolean
  minW: number
  minH: number
}

type Box = { x: number; y: number; w: number; h: number }

function fallbackBox(layout: TileLayout, bp: Breakpoint, index: number): Box {
  const source = layout.lg ?? layout.md ?? layout.sm
  const cols = COLS[bp]

  if (source) return { x: 0, y: source.y, w: Math.min(source.w, cols), h: source.h }

  return { x: 0, y: index * 7, w: cols, h: 7 }
}

export function toGridLayouts(tiles: readonly TileItem[]): Record<Breakpoint, GridItem[]> {
  const out = {} as Record<Breakpoint, GridItem[]>

  for (const bp of BREAKPOINTS) {
    out[bp] = tiles.map((tile, index) => {
      const box = tile.layout[bp] ?? fallbackBox(tile.layout, bp, index)

      return {
        i: tile.id,
        ...box,
        w: Math.min(box.w, COLS[bp]),
        static: false,
        minW: 2,
        minH: 2,
      }
    })
  }

  return out
}

export function unsupportedConfig(tile: TileItem): string | null {
  if (tile.config.overrideFilters !== null) {
    return "This tile overrides the global filters, which the read-only canvas does not apply yet."
  }
  return null
}

/** The layout item RGL hands back. It also carries `moved` / `isDraggable` and other fields we do not care about */
type RglItem = { i: string; x: number; y: number; w: number; h: number; static?: boolean }

export type Layouts = Record<Breakpoint, GridItem[]>

export function normalizeLayouts(all: Partial<Record<Breakpoint, readonly RglItem[]>>): Layouts {
  const out = {} as Layouts

  for (const bp of BREAKPOINTS) {
    out[bp] = (all[bp] ?? []).map((it) => ({
      i: it.i,
      x: it.x,
      y: it.y,
      w: it.w,
      h: it.h,
      static: it.static ?? false,
      minW: 2,
      minH: 2,
    }))
  }

  return out
}

export function sameBoxes(a: readonly GridItem[] = [], b: readonly GridItem[] = []): boolean {
  if (a.length !== b.length) return false
  const byId = new Map(b.map((it) => [it.i, it]))

  return a.every((it) => {
    const other = byId.get(it.i)
    return other != null && other.x === it.x && other.y === it.y && other.w === it.w && other.h === it.h
  })
}

export function changedBreakpoints(draft: Layouts, base: Layouts): Breakpoint[] {
  return BREAKPOINTS.filter((bp) => !sameBoxes(draft[bp], base[bp]))
}

export function toLayoutItems(items: readonly GridItem[]) {
  return items.map(({ i, x, y, w, h }) => ({ tileId: i, x, y, w, h }))
}
